import { useCallback, useMemo, useState } from "react"
import CellCardItem from "./CellCardItem"
import LocationGroup from "./LocationGroup"
import {
   getWearableCardPlaceholderClassName,
   getWearableCardSize,
   gridSettings,
   Location,
} from "./utils"
import WearableCardWrapper from "./WearableCardWrapper"
import Grid from "@/components/shared/Grid"
import { Icon, IconColor } from "@/components/shared/Icon"
import LazyMount from "@/components/shared/LazyMount"
import { useScreenWidth } from "@/hooks/useScreenWidth"
import { Alert } from "@/types/alert"
import { CardSize, Token } from "@/types/index"
import { getErrorMessage } from "@/utils/index"

const WIDTH_SCREEN = 1200

const cellPlaceholderClassName: Record<CardSize, string> = {
   [CardSize.LARGE]: "floor-overview__cell-placeholder--large",
   [CardSize.MEDIUM]: "floor-overview__cell-placeholder--medium",
   [CardSize.SMALL]: "",
}

const getCellCardSize = (): CardSize =>
   (localStorage.getItem(Token.CELL_CARD_SIZE) as CardSize) ?? CardSize.LARGE

const getCellCardPlaceholderClassName = (
   size: CardSize,
   hasActiveAlert: boolean
): string => {
   if (hasActiveAlert) return "floor-overview__cell-placeholder--large"
   return cellPlaceholderClassName[size]
}

interface FloorOverviewContentProps {
   loading: boolean
   error: number | null | undefined
   showSettingsWarning: boolean
   cellIds: string[]
   wearableIds: string[]
   wearableLocationById: Record<string, string>
   locationVisibility: boolean
   wearableAlertsByInmateId: Record<string, Alert[]>
   locations: Location[]
   wearableIdsByLocationId: Record<string, string[]>
   cellIdsWithActiveAlerts: Set<string>
   wearableIdsWithActiveAlert: Set<string>
   onClearAlertClick: (alert: Alert) => void
   onNavigateToCellDetail: (cellId: string) => void
   onNavigateToInmateDetail: (inmateId: string) => void
   onChangeCellStatus: (
      cellId: string,
      status: boolean,
      isMaintenanceField?: boolean
   ) => void
   onSwapDeviceClick: (wearableId: string) => void
   onTriggerTestAlarm?: (cellId: string) => void
}

const FloorOverviewContent = ({
   loading,
   error,
   showSettingsWarning,
   cellIds,
   wearableIds,
   wearableLocationById,
   locationVisibility,
   wearableAlertsByInmateId,
   locations,
   wearableIdsByLocationId,
   cellIdsWithActiveAlerts,
   wearableIdsWithActiveAlert,
   onClearAlertClick,
   onNavigateToCellDetail,
   onNavigateToInmateDetail,
   onChangeCellStatus,
   onSwapDeviceClick,
   onTriggerTestAlarm,
}: FloorOverviewContentProps) => {
   const width = useScreenWidth()
   const smallScreen = useMemo(() => width <= WIDTH_SCREEN, [width])
   const cellCardSize = useMemo(getCellCardSize, [])
   const wearableCardSize = useMemo(getWearableCardSize, [])

   const [expandedCellIds, setExpandedCellIds] = useState<Set<string>>(
      new Set()
   )
   const [everExpandedCellIds, setEverExpandedCellIds] = useState<Set<string>>(
      new Set()
   )
   const [expandedWearableIds, setExpandedWearableIds] = useState<Set<string>>(
      new Set()
   )
   const [everExpandedWearableIds, setEverExpandedWearableIds] = useState<
      Set<string>
   >(new Set())

   const handleCellExpandedChange = useCallback(
      (id: string, isExpanded: boolean) => {
         setExpandedCellIds((prev) => {
            const next = new Set(prev)
            if (isExpanded) next.add(id)
            else next.delete(id)
            return next
         })
         if (isExpanded) {
            setEverExpandedCellIds((prev) =>
               prev.has(id) ? prev : new Set(prev).add(id)
            )
         }
      },
      []
   )

   const handleWearableExpandedChange = useCallback(
      (wearableId: string, isExpanded: boolean) => {
         setExpandedWearableIds((prev) => {
            const next = new Set(prev)
            if (isExpanded) next.add(wearableId)
            else next.delete(wearableId)
            return next
         })
         if (isExpanded) {
            setEverExpandedWearableIds((prev) =>
               prev.has(wearableId) ? prev : new Set(prev).add(wearableId)
            )
         }
      },
      []
   )

   if (!loading && error) {
      return (
         <span className="floor-overview__data--error">
            {getErrorMessage(error)}
         </span>
      )
   }

   if (showSettingsWarning) {
      return (
         <div className="floor-overview__data-warning-info">
            <Icon
               name="alert"
               size="24"
               color={IconColor.XANTHOUS}
            />
            Please complete the settings in order to proceed
         </div>
      )
   }

   return (
      <>
         <Grid
            sx={{
               mx: "33px",
               ...(smallScreen ? gridSettings.small : gridSettings.default),
            }}
         >
            {cellIds.map((id) => (
               <LazyMount
                  key={id}
                  className={getCellCardPlaceholderClassName(
                     cellCardSize,
                     cellIdsWithActiveAlerts.has(id) || expandedCellIds.has(id)
                  )}
               >
                  <CellCardItem
                     id={id}
                     onClearAlertClick={onClearAlertClick}
                     onNavigateToCellDetail={onNavigateToCellDetail}
                     onChangeCellStatus={onChangeCellStatus}
                     onTriggerTestAlarm={onTriggerTestAlarm}
                     isExpanded={expandedCellIds.has(id)}
                     isCollapsed={
                        everExpandedCellIds.has(id) && !expandedCellIds.has(id)
                     }
                     onExpandedChange={handleCellExpandedChange}
                  />
               </LazyMount>
            ))}
            {!locationVisibility &&
               wearableIds.map((wearableId) => {
                  return (
                     <LazyMount
                        key={`wearable-${wearableId}`}
                        className={getWearableCardPlaceholderClassName(
                           wearableCardSize,
                           wearableIdsWithActiveAlert.has(wearableId) ||
                              expandedWearableIds.has(wearableId)
                        )}
                     >
                        <WearableCardWrapper
                           location={wearableLocationById[wearableId]}
                           wearableId={wearableId}
                           alertsByInmateId={wearableAlertsByInmateId}
                           onSwapDeviceClick={onSwapDeviceClick}
                           onClearAlertClick={onClearAlertClick}
                           onNavigateToInmateDetail={onNavigateToInmateDetail}
                           isExpanded={expandedWearableIds.has(wearableId)}
                           isCollapsed={
                              everExpandedWearableIds.has(wearableId) &&
                              !expandedWearableIds.has(wearableId)
                           }
                           onExpandedChange={handleWearableExpandedChange}
                        />
                     </LazyMount>
                  )
               })}
         </Grid>

         {locationVisibility &&
            locations.map((location) => {
               const ids = wearableIdsByLocationId[location.id] ?? []
               // const isOutOfBounds = location.id === "outOfBounds"

               // if (isOutOfBounds && !location.wearables.length) {
               //    return null
               // }

               return (
                  <LocationGroup
                     key={`location-group-${location.id}`}
                     locationId={location.id}
                     locationName={location.name}
                     wearableIds={ids}
                     wearableCardSize={wearableCardSize}
                     wearableIdsWithActiveAlert={wearableIdsWithActiveAlert}
                     expandedWearableIds={expandedWearableIds}
                     everExpandedWearableIds={everExpandedWearableIds}
                     smallScreen={smallScreen}
                     wearableAlertsByInmateId={wearableAlertsByInmateId}
                     onSwapDeviceClick={onSwapDeviceClick}
                     onClearAlertClick={onClearAlertClick}
                     onNavigateToInmateDetail={onNavigateToInmateDetail}
                     onExpandedChange={handleWearableExpandedChange}
                  />
               )
            })}
      </>
   )
}

export default FloorOverviewContent
